import { ApolloClient } from '@apollo/client'
import type { Stripe } from 'stripe'
import { Durations } from '@/constants/durations'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { ContentfulClientOptions } from '../ApolloClient'
import { cacheService } from '../CacheService'
import { getActiveSalesFromContentful, ValidSale } from '../ContentfulSaleService'
import { getOfferById, Offer } from '../PifService'
import { formatPrice } from '../PriceFormatter'
import { findPromotionCodeByCustomerFacingCode } from '../StripeService/StripeCouponService'

export interface ValidatedPromotion {
  offer: Offer
  promotionCode: Stripe.PromotionCode
}

export interface GuildSale extends ValidatedSale {
  category: 'guild'
}

export interface ValidatedSale extends Omit<ValidSale, 'promotionsCollection'> {
  promotions: ValidatedPromotion[]
}

interface GetValidatedActiveSalesArgs {
  opts: ContentfulClientOptions
  federationClient: ApolloClient
}

export async function getGuildSale(args: GetValidatedActiveSalesArgs): Promise<GuildSale | null> {
  try {
    const cacheKey = cacheService.genCacheKey('saleService.getGuildSale', 'current-guild-sale-for-all-users')
    const cached = await cacheService.get(cacheKey)
    if (cached) {
      const cachedGuildSale = JSON.parse(cached)
      logger().debug('Successfully fetched a cached guild sale.', { cachedGuildSale })
      return cachedGuildSale
    }

    const sales = await getValidatedActiveSales(args)
    const guildSales = sales.filter(isGuildSale)
    const guildSale = guildSales[0]

    if (guildSales.length === 0) {
      logger().debug('There are currently no guild sales. Nothing will be returned.')
    } else if (guildSales.length === 1) {
      logger().debug('Successfully fetched an active guild sale.', { guildSale })
    } else {
      logger().error('There are currently multiple active guild sales! Only the first will be used.', {
        guildSale,
        guildSales,
      })
    }

    if (guildSale) {
      const saleEndsInSeconds = guildSale?.endDate
        ? Math.floor((new Date(guildSale.endDate).getTime() - Date.now()) / 1000)
        : Number.MAX_SAFE_INTEGER
      const expireInSeconds = Math.min(Durations.ONE_DAY_IN_SECONDS, saleEndsInSeconds)
      await cacheService.set(cacheKey, JSON.stringify(guildSale), { expireInSeconds })
    }

    return guildSale || null
  } catch (error) {
    logger().error('Error in getGuildSale', { error })
    return null
  }
}

function isGuildSale(sale: ValidatedSale): sale is GuildSale {
  return sale.category === 'guild'
}

async function getValidatedActiveSales({
  opts,
  federationClient,
}: GetValidatedActiveSalesArgs): Promise<ValidatedSale[]> {
  const activeSales = await getActiveSalesFromContentful(opts)
  const results = await Promise.all(
    activeSales.map((sale) => transformContentfulSaleToValidatedSale(sale, federationClient)),
  )
  const validatedSales = results.filter(isDefined)
  if (validatedSales.length)
    logger().debug('Successfully fetched and validated active sale objects.', { validatedSales })
  return validatedSales
}

export async function transformContentfulSaleToValidatedSale(
  sale: ValidSale,
  federationClient: ApolloClient,
): Promise<ValidatedSale | undefined> {
  const promotions = sale.promotionsCollection?.items || []
  if (promotions.length === 0) {
    logger().error('The sale object does not have any promotions! This sale will be disregarded.', { sale })
    return
  }

  const validPromotions: ValidatedPromotion[] = []
  for (const promotion of promotions) {
    const maybeValid = await getValidPromotion(sale, promotion, federationClient)
    if (maybeValid) validPromotions.push(maybeValid)
  }

  if (validPromotions.length === 0) {
    logger().error('None of the promotion objects associated with the sale are valid! This sale will be disregarded.', {
      sale,
    })
    return
  }

  const validatedSale: ValidatedSale = {
    ...sale,
    promotions: validPromotions,
  }
  logger().debug('This sale has been validated.', { sale, validatedSale })
  return validatedSale
}

async function getValidPromotion(
  sale: ValidSale,
  promotion: NonNullable<ValidSale['promotionsCollection']>['items'][number],
  federationClient: ApolloClient,
): Promise<ValidatedPromotion | undefined> {
  if (!promotion) return

  if (!promotion.bimId) {
    logger().error(
      'This promotion is missing a bimId! A product for the promotion cannot be looked up! This promotion will be disregarded.',
      { sale, promotion },
    )
    return
  }

  const offer = await getOfferById({ offerId: promotion.bimId }, federationClient)
  if (!offer) {
    logger().error(
      'A Promotion in Contentful associated with a sale is linked to a bim product id that does not exist! This promotion will be disregarded.',
      { sale, promotion, bimId: promotion.bimId },
    )
    return
  }

  if (!promotion.promoCode) {
    logger().error(
      'This promotion is missing a promoCode! The Stripe PromotionCode cannot be looked up! This promotion will be disregarded.',
      { sale, promotion },
    )
    return
  }

  const promotionCode = await findPromotionCodeByCustomerFacingCode(promotion.promoCode)

  if (!promotionCode) {
    logger().error(
      'Failed to fetch a Stripe Promotion Code for a promotion defined in Contentful associated with a sale. This promotion will be disregarded.',
      { sale, promotion, promoCode: promotion.promoCode },
    )
    return
  }
  if (!promotionCode.active) {
    logger().error(
      'A Promotion in Contentful associated with a sale is linked to a Promotion Code in Stripe that is not active! This promotion will be disregarded.',
      { sale, promotion, promoCode: promotion.promoCode },
    )
    return
  }

  return { offer, promotionCode }
}

export function getPromotionalPrice(promotion: ValidatedPromotion): number | null {
  if (!promotion) return null
  const originalPrice = promotion.offer.price
  let price = originalPrice

  if (promotion.promotionCode.coupon.amount_off && originalPrice - promotion.promotionCode.coupon.amount_off > 0) {
    price = originalPrice - promotion.promotionCode.coupon.amount_off
  } else if (promotion.promotionCode.coupon.percent_off) {
    const discountAmount = (originalPrice * promotion.promotionCode.coupon.percent_off) / 100
    price = originalPrice - discountAmount
  }

  return price
}

export function getPromotionMonthlyDuration(promotion: Stripe.PromotionCode): number {
  if (!promotion) return 0
  if (
    promotion.coupon.duration === 'once' ||
    (promotion.coupon.duration === 'repeating' && promotion.coupon.duration_in_months === 1)
  ) {
    return 1
  }
  if (
    promotion.coupon.duration === 'repeating' &&
    promotion.coupon.duration_in_months &&
    promotion.coupon.duration_in_months > 1
  ) {
    return promotion.coupon.duration_in_months
  }

  return 0
}

export function formatPromotionPriceDisplay(promotion: ValidatedPromotion, locale: string): string | null {
  const price = getPromotionalPrice(promotion)
  if (price === null) return null

  return formatPrice(price, {
    locale,
    currency: promotion.offer.currency,
    includeDecimals: true,
    ignoreEmptyDecimal: true,
  })
}

export function getIsGreaterThan24Hours(sale: GuildSale | null) {
  const now = new Date()
  return Boolean(sale?.endDate && new Date(sale.endDate).getTime() - now.getTime() > 24 * 60 * 60 * 1000)
}
